<template>
  <UIPopup
    v-model="modelValue"
    :closeable="standaloneBooking"
    :max-width="isDesktop ? '900px' : isTablet ? '553px' : ''"
    hide-buttons
    @close="modelValue = false"
  >
    <template #header>
      <UIBaseTransition>
        <h1 v-if="step === 1" class="mt-10 px-8 text-2xl">{{ $t('onboarding.bookCall.step1.title') }}</h1>
        <h1 v-else class="mt-10 px-8 text-2xl">{{ $t('onboarding.bookCall.step2.title') }}</h1>
      </UIBaseTransition>
    </template>
    <template #body>
      <div v-if="step === 1" class="mx-auto mt-4 flex max-w-[384px] flex-col gap-9 desktop:max-w-none">
        <div class="mx-auto flex w-full flex-col gap-8 desktop:flex-row desktop:justify-center desktop:gap-[45px]">
          <div class="flex flex-col gap-10 border-solid desktop:border-r desktop:pr-[45px]">
            <div class="mt-8 flex items-center gap-3 desktop:mt-0 desktop:flex-row">
              <div class="flex h-9 w-1/2 flex-row items-center gap-4 rounded-lg bg-gold-primary-30 px-2.5 py-1.5">
                <UIIcon name="clock" class="text-gold-primary-120" />
                {{ $t('onboarding.bookCall.step1.duration') }}
              </div>
              <div class="flex h-9 w-1/2 flex-row items-center gap-4 rounded-lg bg-gold-primary-30 px-2.5 py-1.5">
                <UIIcon name="phone" class="text-gold-primary-120" />
                {{ $t('onboarding.bookCall.step1.type') }}
              </div>
            </div>
            <DatePicker
              v-model="date"
              :min-date="new Date()"
              title-position="center"
              :locale="{ masks: { weekdays: 'WWW' }, id: locale }"
              :disabled-dates="disabledDates"
              borderless
              color="orange"
              :max-date
              class="size-full"
              :input-debounce="0"
              @update:model-value="selectDate"
            />
          </div>
          <div
            class="flex max-h-[500px] flex-1 flex-col gap-5 overflow-y-auto overflow-x-hidden desktop:max-w-[230px] desktop:gap-10"
          >
            <div class="h-9 w-full gap-4 rounded-[8px] bg-gold-primary-30 px-2.5 py-2 text-center">
              {{ formattedDate }}
            </div>

            <UIBaseTransition>
              <UIProgressSpinner v-if="isLoading" class="mx-auto" />
              <!-- timeslots from 8am to 1am Dubai time, displayed in local time -->
              <div v-else id="time-slots" class="flex h-full flex-col gap-2">
                <div v-for="(slot, index) in availableTimeSlots" :key="index" class="flex flex-row items-center gap-2">
                  <button
                    :id="String(index)"
                    class="h-14 flex-auto select-none rounded-lg border border-solid py-4 text-center text-black-80 transition-all duration-200"
                    :class="[
                      selectedTimeIndex === index
                        ? 'w-2/3 border-gold-primary-100 bg-gold-primary-10'
                        : 'w-full border-black-20',
                    ]"
                    :disabled="isPending"
                    @click="selectedTimeIndex = index"
                  >
                    {{ slot.displayTime }}
                  </button>
                  <Transition name="width">
                    <button
                      v-if="selectedTimeIndex === index"
                      class="flex h-14 w-1/3 items-center justify-center rounded-lg bg-gold-primary-100 py-4 font-bold transition-width"
                      :disabled="isPending"
                      @click="book"
                    >
                      <UIBaseTransition>
                        <UIProgressSpinner v-if="isPending" />
                        <span v-else>{{ $t('onboarding.bookCall.step2.button') }}</span>
                      </UIBaseTransition>
                    </button>
                  </Transition>
                </div>
              </div>
            </UIBaseTransition>
          </div>
        </div>
      </div>
      <div v-else class="mx-auto flex w-full flex-col items-center gap-8 desktop:max-w-[486px]">
        <div class="flex flex-col items-center gap-10">
          <div class="mt-9 text-center text-xl text-black-80">
            {{ $t('onboarding.bookCall.step2.message') }}
          </div>
          <div class="flex flex-col items-center gap-10 desktop:basis-1/2 desktop:flex-row">
            <div class="flex flex-row items-center gap-2">
              <UIIcon name="calendar" class="text-gold-primary-100" />
              {{ formattedDate }}
            </div>
            <div class="flex flex-row items-center gap-2">
              <UIIcon name="clock" class="text-gold-primary-100" />
              {{ formattedDateAndTime }}
            </div>
          </div>
        </div>
        <UIButton id="finish" full-width @click="handleFinish">
          {{ $t('onboarding.bookCall.step2.finish') }}
        </UIButton>
      </div>
    </template>
  </UIPopup>
</template>

<script setup lang="ts">
import enUS from 'date-fns/locale/en-US'
import de from 'date-fns/locale/de'

import { format } from 'date-fns'
import '@angelblanco/v-calendar/style.css'
import { DatePicker } from '@angelblanco/v-calendar'
import { useI18n } from 'vue-i18n'
import { all } from 'country-codes-list'
import { POPUPS } from '@/components/dynamic/maps'

type Props = {
  standaloneBooking?: boolean
}
const props = defineProps<Props>()

const modelValue = defineModel<boolean>()

const { locale } = useI18n()

const { isTablet, isDesktop, isMobile } = useAppBreakpoints()

const date = ref<Date | null>(new Date())
const selectedTimeIndex = ref<number | null>(null)
const maxDate = new Date()
maxDate.setDate(maxDate.getDate() + 7)
const step = ref(1)

// Dubai time offset is UTC+4
const DUBAI_OFFSET = 4 * 60 // 4 hours in minutes
const LOCAL_OFFSET = new Date().getTimezoneOffset() // Local offset in minutes
const OFFSET_DIFF = LOCAL_OFFSET + DUBAI_OFFSET // Difference between local and Dubai time in minutes

const { data, isLoading } = useWorkingHours()

const disabledDates = computed(() => {
  if (!data.value) return []

  const workingDays = data.value.working_days.split(',').map(Number) // e.g., [1,2,3,4,5] for Mon-Fri

  const allDays = [0, 1, 2, 3, 4, 5, 6]
  const disabledDays = allDays.filter((day) => !workingDays.includes(day)).map((day) => day + 1)
  return [
    {
      repeat: {
        weekdays: disabledDays,
      },
    },
  ]
})

const selectDate = (newDate: Date) => {
  date.value = newDate
  selectedTimeIndex.value = null
  generateTimeSlots()

  // scroll to time-slots
  if (isMobile) {
    nextTick(() => {
      const timeSlots = document.getElementById('time-slots')
      if (timeSlots) {
        timeSlots.scrollIntoView({ behavior: 'smooth' })
      }
    })
  }
}

// Convert Dubai hour to local hour
const dubaiToLocalHour = (dubaiHour: number, dubaiMinutes: number = 0): { hour: number; minutes: number } => {
  const dubaiTimeInMinutes = dubaiHour * 60 + dubaiMinutes
  const localTimeInMinutes = dubaiTimeInMinutes - OFFSET_DIFF

  let localHour = Math.floor(localTimeInMinutes / 60)
  const localMinutes = ((localTimeInMinutes % 60) + 60) % 60 // Ensure positive minutes

  // Adjust for day boundaries
  if (localHour < 0) localHour += 24
  if (localHour >= 24) localHour -= 24

  return { hour: localHour, minutes: localMinutes }
}

const allTimeSlots = ref<
  {
    dubaiHour: number
    dubaiMinutes: number
    displayTime: string
  }[]
>([])

const generateTimeSlots = () => {
  const interval = 30 // minutes
  const slots = []
  // Enforce slots only in Dubai working hours and in working days
  if (!data.value) return (allTimeSlots.value = [])
  const [fromHour, fromMinutes] = data.value.working_hours_from.split(':').map(Number)
  const [toHour, toMinutes] = data.value.working_hours_to.split(':').map(Number)
  const workingDays = data.value.working_days.split(',').map(Number) // e.g., [1,2,3,4,5] for Mon-Fri
  // Generate slots for the current day only
  if (date.value) {
    const dayOfWeek = date.value.getDay() // 0 (Sun) to 6 (Sat)
    const dayOfWeekISO = dayOfWeek === 0 ? 7 : dayOfWeek // Convert to ISO (1=Mon, 7=Sun)
    if (!workingDays.includes(dayOfWeekISO)) {
      return (allTimeSlots.value = []) // No slots if the selected day is not a working day
    }
    let currentHour = fromHour
    let currentMinutes = fromMinutes
    while (
      currentHour < toHour ||
      (currentHour === toHour && currentMinutes <= toMinutes) ||
      (toHour < fromHour && (currentHour > fromHour || (currentHour === fromHour && currentMinutes >= fromMinutes)))
    ) {
      const { hour: localHour, minutes: localMinutes } = dubaiToLocalHour(currentHour, currentMinutes)
      slots.push({
        dubaiHour: currentHour,
        dubaiMinutes: currentMinutes,
        displayTime: `${String(localHour).padStart(2, '0')}:${String(localMinutes).padStart(2, '0')}`,
      })
      currentMinutes += interval
      if (currentMinutes >= 60) {
        currentMinutes = 0
        currentHour += 1
      }
      if (currentHour >= 24) {
        currentHour = 0
      }
    }
  }
  allTimeSlots.value = slots
}

watch(
  () => data.value,
  (newData) => {
    if (!newData?.working_days) return
    generateTimeSlots()
    // if not slots today, select next available day and generate slots
    if (availableTimeSlots.value.length === 0) {
      if (date.value) {
        const today = new Date(date.value)
        for (let i = 1; i <= 7; i++) {
          const nextDate = new Date(today)
          nextDate.setDate(today.getDate() + i)
          const dayOfWeek = nextDate.getDay() // 0 (Sun) to 6 (Sat)
          const dayOfWeekISO = dayOfWeek === 0 ? 7 : dayOfWeek // Convert to ISO (1=Mon, 7=Sun)
          if (data.value?.working_days?.split(',')?.map(Number)?.includes(dayOfWeekISO)) {
            date.value = nextDate
            generateTimeSlots()
            break
          }
        }
      }
    }
  },
  { immediate: true }
)

const isDatePast = (time: string) => {
  const [hour, minute] = time.split(':')
  const dateWithTime = new Date(date.value!)
  dateWithTime.setHours(Number(hour))
  dateWithTime.setMinutes(Number(minute))

  const today = new Date()

  return dateWithTime < today
}

const availableTimeSlots = computed(() => {
  return allTimeSlots.value.filter((slot) => !isDatePast(slot.displayTime))
})

const formattedDate = computed(() => {
  return date.value
    ? format(date.value, 'EEEE, d MMMM', {
        locale: locale.value === 'de' ? de : enUS, // Use German locale if set, otherwise English
      })
    : ''
})

const formattedDateAndTime = computed(() => {
  if (!date.value || selectedTimeIndex.value === null || !availableTimeSlots.value[selectedTimeIndex.value]) return ''

  const currentSlot = availableTimeSlots.value[selectedTimeIndex.value]
  const nextSlotIndex = selectedTimeIndex.value + 1
  const nextSlot = availableTimeSlots.value[nextSlotIndex]

  return `${currentSlot.displayTime} - ${nextSlot ? nextSlot.displayTime : '01:00'}`
})

const { mutateAsync: bookCall, isPending } = useBookCall()

const { catchErrors } = useErrorHandler()

const gtm = useGtm()
const { sendEventToBackend } = useTracking()

const authStore = useAuthStore()
const uiStore = useUIStore()

const book = () => {
  if (selectedTimeIndex.value === null || isPending.value || !availableTimeSlots.value[selectedTimeIndex.value]) return

  const selectedSlot = availableTimeSlots.value[selectedTimeIndex.value]
  const dateWithTime = new Date(date.value!)

  const [hour, minute] = selectedSlot.displayTime.split(':')
  dateWithTime.setHours(Number(hour))
  dateWithTime.setMinutes(Number(minute))
  dateWithTime.setSeconds(0)
  dateWithTime.setMilliseconds(0)

  bookCall({
    date: dateWithTime.toISOString(),
    dubaiTime: `${String(selectedSlot.dubaiHour).padStart(2, '0')}:${String(selectedSlot.dubaiMinutes).padStart(
      2,
      '0'
    )}`,
  })
    .then(() => {
      if (!props.standaloneBooking) {
        gtm?.trackEvent({ event: 'meeting-scheduled' })
      }
      sendEventToBackend('meeting_scheduled', {
        crm_user_id: authStore.user?.id,
        email: authStore.user?.email,
        phone: authStore.user?.phone,
        fname: authStore.user?.firstName,
        lname: authStore.user?.lastName,
        meeting_date: dateWithTime.toISOString(),
      })
      step.value = 2
    })
    .catch(catchErrors)
}

const handleFinish = () => {
  modelValue.value = false
  if (!props.standaloneBooking && !authStore.user?.phoneVerified) {
    nextTick(() => {
      uiStore.showPopup(POPUPS.VERIFY_PHONE)
    })
  }
}
</script>

<style scoped lang="scss">
:deep(.vc-container) {
  width: 100%;
  .vc-weeks {
    padding-top: 32px;
  }
  .vc-weekday {
    font-weight: bold !important;
    color: #3c413f;
    width: 40px;
    height: 40px;
    font-weight: 400;
    font-size: 14px;
    @media screen and (min-width: 768px) {
      width: 48px;
      height: 48px;
    }
  }
  .vc-day {
    @media screen and (min-width: 768px) {
      width: 40px;
      height: 40px;
    }
    .vc-day-content {
      width: 100%;
      height: 100%;
    }
    .vc-highlight {
      width: 100%;
      height: 100%;
    }
  }
  .vc-focusable:not(.vc-disabled) {
    background: #eacf9c66;
  }

  .vc-arrow {
    background-color: #ffb648;
    color: white;
    border-radius: 100%;
    width: 36px;
    height: 36px;
  }

  .vc-orange {
    --vc-focus-ring: #f2d5a4;
    --vc-highlight-outline-content-color: white;
    --vc-highlight-light-content-color: #393939;
    --vc-highlight-outline-bg: #f2d5a4;
    --vc-accent-200: #fbf2e3;
    --vc-accent-600: #ffb648;
    --vc-accent-900: #747474;
  }
  .vc-week {
    margin: 8px 0;
    gap: 15px;
  }
  .vc-weekdays {
    font-size: 13px;
    gap: 8px;
  }
}
</style>
